import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContextType, User } from '../types';
import { authAPI } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);

  const login = async (email: string, password: string, recaptchaToken?: string | null) => {
    setLoading(true);
    try {
      const res = await authAPI.login(email, password, recaptchaToken);
      const { token: jwt, data } = res;
      localStorage.setItem('token', jwt);
      localStorage.setItem('user', JSON.stringify(data));
      setToken(jwt);
      setUser(data);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // Attempt to refresh user on mount if token exists
  useEffect(() => {
    const init = async () => {
      if (!token) return;
      try {
        const res = await authAPI.getMe();
        if (res.success && res.data) {
          setUser(res.data as unknown as User);
          localStorage.setItem('user', JSON.stringify(res.data));
        }
      } catch {
        logout();
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ user, token, login, logout, loading }), [user, token, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
